'use client'

import React from 'react'
import { cn } from '../../../../../lib/utils'
import s from '../../../../styles/animations.module.css'
import { Avatar, AvatarFallback, AvatarImage } from '../../../Avatar'
import { Button, type ButtonProps } from '../../../Button'
import { Chip } from '../../../Chip'

export type AppTileAction = ButtonProps & { label: string }

export type AppTileProps = {
  id: string
  title: string
  description: string
  logo?: {
    img?: string
    initials?: string
    seed?: string
  }
  isSelected?: boolean
  /** Primary tile action (Add / Remove). */
  action?: AppTileAction
  /** @deprecated Use `action`. Kept so existing schemas keep compiling. */
  install?: AppTileAction
  handleClick?: () => void
  category?: string
  rating?: number
  installsCount?: number
  isFeatured?: boolean
  tags?: string[]
  publisherName?: string
}

export const AppTile = (props: AppTileProps) => {
  const {
    id,
    title,
    description,
    logo,
    isSelected,
    action,
    install,
    handleClick,
    isFeatured,
  } = props

  const tileAction = action ?? install

  return (
    <div
      className={cn(
        'group flex h-full w-full cursor-pointer items-center gap-3 rounded-xl border border-border/70 bg-card px-3.5 py-3',
        'hover:bg-muted/50',
        { 'bg-accent': isSelected },
        s.smallTransitionIn,
      )}
      title={`list-tile-${id}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return
        handleClick?.()
      }}
    >
      <Avatar className="h-10 w-10 shrink-0 rounded-lg">
        <AvatarImage src={logo?.img} seed={logo?.seed} />
        <AvatarFallback className="rounded-lg text-xs">
          {logo?.initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-1.5">
          <div className="truncate text-sm font-medium text-foreground">
            {title}
          </div>
          {isFeatured && (
            <Chip label="Featured" variant="complete" className="text-[10px]" />
          )}
        </div>
        {description ? (
          <div className="truncate text-xs text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>

      {tileAction && (
        <Button
          size="sm"
          variant={tileAction.variant ?? 'outline'}
          className="shrink-0"
          {...tileAction}
          label={tileAction.label}
        />
      )}
    </div>
  )
}

export type { ButtonProps as AppTileInstallButtonProps } from '../../../Button'
